import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { AxiosError } from "axios";
import type { AgendaType } from "../../../../hooks/agenda";
import { bookingPath } from "../../../atoms/BookingLinkButton";
import { durationLabel } from "./agendaDates";
import { plainTextOf, RICH_TEXT_LIMITS } from "../../../../utils/richText";
import { APPOINTMENT_LIMITS } from "./appointmentDraft";

export const BOOKING_LIMITS = {
  minDurationMin: 15,
  maxDurationMin: 480,
  maxNoticeHours: 720,
  maxHorizonDays: 90,
  maxTitleLength: 200,
  maxDescriptionLength: RICH_TEXT_LIMITS.appointmentTypeDescription,
  maxLocationLength: APPOINTMENT_LIMITS.location,
  maxMembers: 50,
} as const;

export const DURATION_MINUTE_STEPS = [0, 15, 30, 45];

export const durationParts = (minutes: number) => ({
  hours: Math.floor(minutes / 60),
  minutes: minutes % 60,
});

const WHOLE_HOURS = /^\d*$/;

/** The hours as typed; empty ones count as none, so "45 min" needs no "0" typed before it. */
export const durationOf = (hours: string, minutes: number) =>
  Number(hours.trim() || 0) * 60 + minutes;

export const durationProblem = (
  hours: string,
  minutes: number
): "notWhole" | "tooShort" | "tooLong" | null => {
  if (!WHOLE_HOURS.test(hours.trim())) return "notWhole";
  const total = durationOf(hours, minutes);
  if (total > BOOKING_LIMITS.maxDurationMin) return "tooLong";
  if (total < BOOKING_LIMITS.minDurationMin) return "tooShort";
  return null;
};

export const AGENDA_PATH = "/control-panel/agenda";
export const AGENDA_TYPES_PATH = `${AGENDA_PATH}?section=types`;

export const agendaTypePath = (typeId: string) =>
  `${AGENDA_TYPES_PATH}&type=${encodeURIComponent(typeId)}`;

export const bookingTunnelUrl = (entityId: string, typeId: string) =>
  `${process.env.REACT_APP_HOST_URL}${bookingPath(entityId, typeId)}`;

export const bookingPageUrl = (entityId: string) =>
  `${process.env.REACT_APP_HOST_URL}/booking/${entityId}`;

export const descriptionExcerpt = plainTextOf;

export const offersMember = (type: AgendaType, userId: string) =>
  type.active && type.members.some((m) => m.user_identification === userId);

const EXPLAINED_REFUSALS = [
  "BOOKING_PERSON_LOCKED",
  "SLOT_TAKEN",
  "TOO_LONG",
  "PAYMENT_NOT_IN_PLAN",
  "PAYMENT_PENDING",
];

export const useAgendaFailure = () => {
  const { t } = useTranslation();
  return useCallback(
    (error: AxiosError<any> | null | undefined, fallbackKey: string) => {
      const reason: string | undefined = error?.response?.data?.element?.reason;
      return t(
        reason && EXPLAINED_REFUSALS.includes(reason)
          ? `agenda.refusals.${reason}`
          : fallbackKey
      ) as string;
    },
    [t]
  );
};

export const useFormatDuration = () => {
  const { t } = useTranslation();
  return useCallback(
    (minutes: number) => durationLabel(t, "agenda.types.duration", minutes),
    [t]
  );
};
